import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Search } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { PageHeader, Loading, ErrorBox, Empty, Field, useSort } from "@/components/common";
import { api, errMsg, fmtDate } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";

function ClientForm({ open, onOpenChange, onSaved }) {
  const { tx } = useI18n();
  const [f, setF] = useState({ name: "", sector: "", jurisdiction: "Mauritius", sites: "", units: "", mission_contact: "", scope: "", nda_signed: false, retention_years: 7 });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    try {
      const body = { ...f, sites: f.sites.split(",").map((s) => s.trim()).filter(Boolean), units: f.units.split(",").map((s) => s.trim()).filter(Boolean), retention_years: Number(f.retention_years) };
      const { data } = await api.post("/clients", body);
      toast.success(tx("Client créé", "Client created"));
      onSaved(data);
      onOpenChange(false);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="client-form-dialog" className="max-w-lg">
        <DialogHeader><DialogTitle className="text-[#153A63]">{tx("Nouveau client", "New client")}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Field label={tx("Raison sociale", "Company name")}><Input data-testid="client-name-input" value={f.name} onChange={set("name")} /></Field></div>
          <Field label={tx("Secteur", "Sector")}><Input data-testid="client-sector-input" value={f.sector} onChange={set("sector")} /></Field>
          <Field label={tx("Juridiction", "Jurisdiction")}><Input data-testid="client-jurisdiction-input" value={f.jurisdiction} onChange={set("jurisdiction")} /></Field>
          <Field label={tx("Sites (séparés par virgule)", "Sites (comma separated)")}><Input data-testid="client-sites-input" value={f.sites} onChange={set("sites")} /></Field>
          <Field label={tx("Unités / BU", "Units / BU")}><Input data-testid="client-units-input" value={f.units} onChange={set("units")} /></Field>
          <Field label={tx("Contact de mission", "Mission contact")}><Input data-testid="client-contact-input" value={f.mission_contact} onChange={set("mission_contact")} /></Field>
          <Field label={tx("Rétention (années)", "Retention (years)")}><Input type="number" data-testid="client-retention-input" value={f.retention_years} onChange={set("retention_years")} /></Field>
          <div className="col-span-2"><Field label={tx("Périmètre", "Scope")}><Input data-testid="client-scope-input" value={f.scope} onChange={set("scope")} /></Field></div>
          <label className="col-span-2 flex items-center gap-2 text-sm"><Switch data-testid="client-nda-switch" checked={f.nda_signed} onCheckedChange={(v) => setF({ ...f, nda_signed: v })} />{tx("Accord de confidentialité signé", "Confidentiality agreement signed")}</label>
          <p className="col-span-2 text-xs text-slate-500">{tx("Un audit est créé automatiquement ; vous arrivez ensuite sur l'import du fichier des effectifs.", "An audit is created automatically; you then land on the workforce file import.")}</p>
        </div>
        <DialogFooter><Button data-testid="client-save-btn" onClick={save} disabled={f.name.length < 2} className="bg-[#153A63]">{tx("Créer", "Create")}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Clients() {
  const { tx } = useI18n();
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const [archived, setArchived] = useState(false);
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const load = useCallback(() => {
    api.get("/clients", { params: { q: q || undefined, include_archived: archived } }).then((r) => setRows(r.data)).catch((e) => setErr(errMsg(e)));
  }, [q, archived]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  const { sorted, SortTh } = useSort(rows, "name");
  const canCreate = ["admin", "lead"].includes(user.platform_role) || user.can_create_clients;
  return (
    <div>
      <PageHeader eyebrow={tx("Portefeuille", "Portfolio")} title={tx("Clients", "Clients")}
        subtitle={tx("Seuls les clients liés à vos missions autorisées sont visibles.", "Only clients linked to your authorised missions are visible.")}
        actions={canCreate && <Button data-testid="new-client-btn" onClick={() => setOpen(true)} className="bg-[#153A63]"><Plus className="h-4 w-4 mr-1" />{tx("Nouveau client", "New client")}</Button>} />
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative"><Search className="h-4 w-4 absolute left-2.5 top-2.5 text-slate-400" /><Input data-testid="clients-search-input" className="pl-8 w-72" placeholder={tx("Rechercher…", "Search…")} value={q} onChange={(e) => setQ(e.target.value)} /></div>
        <label className="flex items-center gap-2 text-sm text-slate-600"><Switch data-testid="clients-archived-switch" checked={archived} onCheckedChange={setArchived} />{tx("Inclure archivés", "Include archived")}</label>
      </div>
      {err && <ErrorBox error={err} />}
      {!rows ? <Loading /> : rows.length === 0 ? <div className="card-surface"><Empty text={tx("Aucun client", "No clients")} /></div> : (
        <div className="card-surface overflow-x-auto">
          <table className="tbl" data-testid="clients-table">
            <thead><tr><SortTh k="name">{tx("Raison sociale", "Name")}</SortTh><SortTh k="sector">{tx("Secteur", "Sector")}</SortTh><SortTh k="jurisdiction">{tx("Juridiction", "Jurisdiction")}</SortTh><SortTh k="audit_count">Audits</SortTh><SortTh k="created_at">{tx("Créé", "Created")}</SortTh><th /></tr></thead>
            <tbody>
              {sorted.map((c) => (
                <tr key={c.id} data-testid={`client-row-${c.id}`}>
                  <td><Link to={`/clients/${c.id}`} className="font-medium text-[#2176AE] hover:underline">{c.name}</Link></td>
                  <td>{c.sector || "—"}</td><td>{c.jurisdiction}</td><td className="font-mono">{c.audit_count}</td>
                  <td className="text-xs text-slate-500">{fmtDate(c.created_at, false)}</td>
                  <td>{c.archived_at && <span className="text-xs rounded bg-zinc-100 px-2 py-0.5">{tx("Archivé", "Archived")}</span>}{c.demo && <span className="ml-1 text-[10px] rounded bg-amber-50 border border-amber-200 text-amber-800 px-1.5">DEMO</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {open && <ClientForm open={open} onOpenChange={setOpen} onSaved={(c) => nav(`/audits/${c.first_audit_id}?tab=workforce`)} />}
    </div>
  );
}
